import React, { CSSProperties, PureComponent } from 'react';
import { Flex } from '../../../common/Flex';
import {
    Airspace,
    AirspaceList,
    AirspaceListRequest,
    AirspaceRef,
    AirspaceType,
    FlightDto,
    FlightModelType,
    MapFlightsDataDto,
    MapTrafficVolume,
    MapTrafficVolumeListRequest,
    RegulationOrMcdmOnlyDto,
    RegulationState,
    ScenarioAttributesDto,
    TimePeriod,
    TrafficType,
} from 'app/model/CORE';
import {
    MonitoredMeasure,
    SOL49_MAP_FLIGHTS_DATA_REQUEST_DTO_CLAZZ_VALUE,
    Sol49MapFlightsDataRequestDto,
} from 'app/model/SOL49';
import AppContext from '../../../../common/context/AppContext';
import { ServiceFactory } from 'app/service/ServiceFactory';
import { notification } from 'antd';
import { FlightMapOptions } from '../../../map/model/MapModel';
import * as style from 'ol/style';
import OLMapStyles from '../../../map/ol/OLMapStyles';
import FlightLevelUtils from 'app/common/util/FlightLevelUtils';
import MapUtils, { MonitorableMapMeasure } from '../../../map/util/MapUtils';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from 'app/redux/store';
import { setLoadConvection } from 'app/redux/features/dashboard/dashboard-slice';
import TimeUtils from '../../../../common/util/TimeUtils';
import { TimeSelector } from '../../../map/control/TimeSelector';
import {
    EnvironmentLayerUtils,
    MapLayer,
    MapLayerType,
    MapLayerUtils,
    MapObjectType,
    ObjectToDisplay,
    RegulatedLayerDelayLevelEnum,
} from 'app/components/dashboard/network/map/Map';
import MeasureUtils from 'app/common/util/MeasureUtils';
import LayerSelectionEntry from 'app/components/dashboard/network/map/LayerSelectionEntry';
import {
    MapTrafficVolumeCacheType,
    MapTrafficVolumeCacheUpdated,
} from 'app/components/dashboard/network/map/MapTrafficVolumeCache';
import { LayerUpdateEvent, ShowMapLayerEvent } from 'app/common/eventbus/Events';
import difference from 'lodash/difference';
import { LayerSelectionEntryShortcut } from 'app/components/dashboard/network/map/LayerSelectionEntryShortcut';
import memoizeOne from 'memoize-one';
import { UnityApplication } from 'app/unity/UnityApplication';
import { NMUI4DContext, NMUI4DContextProps } from 'app/common/context/NMUI4DContext';

// Redux Connection
const mapDispatchToProps = {
    setLoadConvection,
};
const mapStateToProps = (state: RootState) => {
    return {
        mapConvectionRedux: state.dashboardData.convectionData,
        monitoredMeasures: state.dashboardData.dashboardData.monitoredMeasures,
        tvCache: state.mapFullscreenData.tvCache,
    };
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface ScenarioMapPanelProps extends PropsFromRedux {
    selectedScenario: ScenarioAttributesDto;
    selectedFlights?: FlightDto[];
    selectedFlightsSegments?: FlightDto[];
    selectedTimePeriod: TimePeriod;
}
interface ScenarioMapPanelState {
    mapFlightsData?: MapFlightsDataDto;
    airspaceData?: AirspaceList;
    tvData?: MapTrafficVolume[];
    refTime: Date;
    displayConvection: boolean;
    displayOffload: boolean;
    displayOnload: boolean;
    displayTv: boolean;
    displayAltRoutes: boolean;
    measureLayer: MapLayer;
}
// https://www.public.nm.eurocontrol.int/PUBPORTAL/gateway/spec/PORTAL.27.2.0.2.23/_res/1716644703748_AW2076-20240525-134500.jpg
// is the image on NOP for ScenarioImage/scenario_repository/PO1/AW_2076_EDUUUTAS.1.jpg
class ScenarioMapPanel extends PureComponent<ScenarioMapPanelProps, ScenarioMapPanelState> {
    private mapId: number;

    private static flightMapOptions: FlightMapOptions = {
        autoZoom: false,
        listVectorPoints: true,
        showActiveAirspaceOnly: true,
        showActiveDynamicVolumeInVpd: false,
        showActiveDynamicVolumeOnly: false,
        showElementaryAirspaceOnly: true,
        showIfpzPartOfTrajectoryOnly: false,
    };

    public static contextType = NMUI4DContext;
    private nmui4dContext?: NMUI4DContextProps;

    constructor(props: ScenarioMapPanelProps) {
        super(props);
        this.state = {
            refTime: this.props.selectedTimePeriod.from,
            displayConvection: true,
            displayOffload: true,
            displayOnload: true,
            displayTv: true,
            displayAltRoutes: true,
            measureLayer: this.initMeasureLayer(),
        };
    }

    componentDidMount() {
        this.nmui4dContext = this.context as NMUI4DContextProps;

        this.updateScenarioMapDisplay(this.props.selectedScenario);
        if (this.props.selectedFlights) {
            this.updateScenarioFlightsMapDisplay(this.props.selectedFlights);
        }
        this.initBusEventListeners();
        this.updateMeasureLayerDisplay(this.props.tvCache);
    }

    componentWillUnmount() {
        AppContext.eventBus.unregister(LayerUpdateEvent.NAME, this.layerUpdateEventCallBack);
        AppContext.eventBus.unregister(MapTrafficVolumeCacheUpdated.NAME, this.mapTrafficVolumeCacheUpdatedCallBack);
    }

    private layerUpdateEventCallBack = (event: LayerUpdateEvent) => {
        // This event is triggered by the LayerSelectionEntry (change of visibility or customProperties)
        // Update the layer and trigger rerender
        if (event.mapId === this.mapId && event.layer.id === this.state.measureLayer.id) {
            this.setState({ measureLayer: event.layer }, () => this.updateMeasureLayerDisplay(this.props.tvCache));
        }
    };

    private mapTrafficVolumeCacheUpdatedCallBack = (event: MapTrafficVolumeCacheUpdated) => {
        if (this.hasRelevantCacheUpdates(event.previousCache, event.cache)) {
            //only refresh map if the new cache value has relevant data for the layer
            this.updateMeasureLayerDisplay(event.cache);
        }
    };

    initBusEventListeners() {
        AppContext.eventBus.register(LayerUpdateEvent.NAME, this.layerUpdateEventCallBack);
        AppContext.eventBus.register(MapTrafficVolumeCacheUpdated.NAME, this.mapTrafficVolumeCacheUpdatedCallBack);
    }

    private hasRelevantCacheUpdates(prevCache: MapTrafficVolumeCacheType, cache: MapTrafficVolumeCacheType) {
        const layerTvIds = this.props.monitoredMeasures.map(mm => MeasureUtils.getTvLocation(mm.measure).id);
        const prevCacheTvIds = Object.keys(prevCache).filter(tvId => layerTvIds.includes(tvId));
        const cacheTvIds = Object.keys(cache).filter(tvId => layerTvIds.includes(tvId));
        return difference(cacheTvIds, prevCacheTvIds).length > 0;
    }

    componentDidUpdate(
        prevProps: Readonly<ScenarioMapPanelProps>,
        prevState: Readonly<ScenarioMapPanelState>,
        snapshot?: any,
    ) {
        if (
            this.props.selectedScenario !== prevProps.selectedScenario ||
            this.state.displayOnload !== prevState.displayOnload ||
            this.state.displayOffload !== prevState.displayOffload ||
            this.state.displayAltRoutes !== prevState.displayAltRoutes
        ) {
            this.updateScenarioMapDisplay(this.props.selectedScenario);
        }
        if (this.props.selectedFlights !== prevProps.selectedFlights) {
            this.updateScenarioFlightsMapDisplay(this.props.selectedFlights);
        }
        if (this.props.selectedTimePeriod !== prevProps.selectedTimePeriod) {
            this.updateMeasureLayerDisplay(this.props.tvCache);
            this.setState({
                refTime: this.props.selectedTimePeriod.from,
            });
        }
    }

    private initMeasureLayer() {
        return {
            objectsToDisplay: MapLayerUtils.toObjectToDisplayArray(
                this.getActiveMeasures(this.props.monitoredMeasures),
                MapObjectType.MEASURE,
            ),
            selection: [],
            label: 'Measure',
            mapType: [MapObjectType.MEASURE],
            priority: 5.1,
            showLabel: false,
            active: false,
            type: MapLayerType.MEASURE,
            id: 2,
            customProperties: {
                [EnvironmentLayerUtils.REGULATED_SECTORS]: false,
                [EnvironmentLayerUtils.REGULATED_LAYER_MIN_DELAY]: RegulatedLayerDelayLevelEnum.ANY,
            },
        };
    }

    private getActiveMeasures = memoizeOne((monitoredMeasures: MonitoredMeasure[]) => {
        return monitoredMeasures.filter(({ measure }) => {
            return (
                MeasureUtils.isRegulation(measure) &&
                (measure.regulationState === RegulationState.APPLIED ||
                    measure.regulationState === RegulationState.APPLYING)
            );
        });
    });

    private getRegulationMapObjects = memoizeOne(
        (objectsToDisplay: ObjectToDisplay[], tvCache: MapTrafficVolumeCacheType) => {
            return objectsToDisplay.map((obj, index) => this.getRegulationMapObject(obj, index, tvCache));
        },
    );

    private updateMeasureLayerDisplay(tvCache: MapTrafficVolumeCacheType) {
        const objectsToDisplay = this.state.measureLayer.objectsToDisplay;
        const missingTvs = LayerSelectionEntry.retrievesRequiredDefinitions(tvCache, objectsToDisplay);
        if (!missingTvs) {
            console.log('ScenarioMap mapId: ', this.mapId);
            ShowMapLayerEvent.fire(
                this.mapId,
                this.state.measureLayer,
                this.getRegulationMapObjects(objectsToDisplay, tvCache),
                this.props.selectedTimePeriod,
                { min: FlightLevelUtils.ON_GROUND_LEVEL, max: FlightLevelUtils.UNLIMITED_LEVEL },
            );
        }
    }

    private getRegulationMapObject = (obj: ObjectToDisplay, index: number, cache: MapTrafficVolumeCacheType) => {
        return {
            measure: obj.object,
            index: index,
            mapTv: cache[(obj.object['measure'] as RegulationOrMcdmOnlyDto).location.id],
            selected: false,
        } as MonitorableMapMeasure;
    };

    private updateScenarioMapDisplay(scenario: ScenarioAttributesDto) {
        this.setState({
            airspaceData: null,
            tvData: null,
            mapFlightsData: null,
        });
        const airspaceReferences: AirspaceRef[] = [];
        if (this.state.displayOnload && scenario.onLoadAirspaceIds) {
            airspaceReferences.push(
                ...scenario.onLoadAirspaceIds.map(airspaceId => {
                    return {
                        id: airspaceId,
                        type: AirspaceType.ES,
                    } as AirspaceRef;
                }),
            );
        }
        if (this.state.displayOffload && scenario.offLoadAirspaceIds) {
            airspaceReferences.push(
                ...scenario.offLoadAirspaceIds.map(airspaceId => {
                    return {
                        id: airspaceId,
                    } as AirspaceRef;
                }),
            );
        }

        if (airspaceReferences.length > 0) {
            this.resilientAirspaceRetrieval(airspaceReferences);
        }

        if (this.state.displayTv) {
            if (this.props.selectedScenario.scenarioPublishedTrafficVolumeIds?.length) {
                const request: MapTrafficVolumeListRequest = {
                    tvIdList: this.props.selectedScenario.scenarioPublishedTrafficVolumeIds.map(tv => tv),
                    dataset: AppContext.getDataset(),
                };
                ServiceFactory.coreService()
                    .getMapTrafficVolumeList(request)
                    .then(result => {
                        if (!result.error) {
                            this.setState({
                                tvData: result.data,
                            });
                        }
                    });
            }
        }
    }

    private resilientAirspaceRetrieval(airspaceReferences: AirspaceRef[]) {
        ServiceFactory.coreService()
            .getAirspaceList({
                dataset: AppContext.getDataset(),
                airspaceReferences: airspaceReferences,
            } as AirspaceListRequest)
            .then(asReply => {
                if (asReply.error) {
                    throw new Error(asReply.error.message);
                } else {
                    this.setState({
                        airspaceData: asReply.data,
                    });
                }
            })
            .catch(reason => {
                if (reason.message.includes('Unknown airspace') && reason.message != null) {
                    const filteredAirspaceRefs = airspaceReferences.filter(
                        ref => !(reason.message as string).includes(ref.id),
                    );
                    if (filteredAirspaceRefs.length < airspaceReferences.length) {
                        this.resilientAirspaceRetrieval(filteredAirspaceRefs);
                    }
                }
            });
    }

    private updateScenarioFlightsMapDisplay(flights: FlightDto[]) {
        ServiceFactory.sol49Service()
            .getMapFlightsData({
                clazz: SOL49_MAP_FLIGHTS_DATA_REQUEST_DTO_CLAZZ_VALUE,
                dataset: AppContext.getDataset(),
                flightProfileTrafficType: TrafficType.LOAD,
                flights: flights.map(flight => flight.flightId),
            } as Sol49MapFlightsDataRequestDto)
            .then(flightsMapReply => {
                if (flightsMapReply.error) {
                    throw new Error(flightsMapReply.error.message);
                } else {
                    this.setState({
                        mapFlightsData: flightsMapReply.data,
                    });
                }
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            });
    }

    private handleMapRefTimeChange = (selectedRefTime: Date) => {
        this.setState({
            refTime: selectedRefTime,
        });
    };

    render() {
        // NOTE(bjarne): In mapless mode the UnityApplication is already created in App.tsx.
        return (
            <Flex direction="vertical" className="layout-scenario-map-id applicable-scenarios-sub-panel">
                {this.renderMenu()}
                {!this.nmui4dContext?.isMapless && <UnityApplication></UnityApplication>}
            </Flex>
        );
    }

    renderMenu() {
        const timePeriodInMinutes =
            (this.props.selectedTimePeriod.to.getTime() - this.props.selectedTimePeriod.from.getTime()) / (60 * 1000);
        let timeSelectorMark: number;
        if (timePeriodInMinutes > 240) {
            timeSelectorMark = 30;
        } else if (timePeriodInMinutes > 120) {
            timeSelectorMark = 20;
        } else if (timePeriodInMinutes > 60) {
            timeSelectorMark = 15;
        } else if (timePeriodInMinutes > 30) {
            timeSelectorMark = 10;
        } else {
            timeSelectorMark = 5;
        }
        return (
            <Flex size="9em" direction="vertical" className={'header'}>
                <Flex direction={'vertical'} style={{ gap: '1em', alignSelf: 'center' }}>
                    <Flex
                        direction={'horizontal'}
                        style={{ gap: '0.5em', alignItems: 'center' }}
                        className={'scenario-map-selection'}>
                        <Flex>Time Selection:</Flex>
                        <TimeSelector
                            width="13em"
                            timePeriod={this.props.selectedTimePeriod}
                            value={this.state.refTime}
                            onChange={this.handleMapRefTimeChange}
                            currentTickColor={'#60B1CC'}
                            markIntervalMs={timeSelectorMark * TimeUtils.MIN_TO_MS}
                        />
                        {this.renderMenuButton(this.state.displayTv, 'TV', () =>
                            this.setState(state => {
                                return { displayTv: !state.displayTv };
                            }),
                        )}
                        {this.state.measureLayer && (
                            <LayerSelectionEntryShortcut
                                minWidthEm={'10em'}
                                mapId={this.mapId}
                                layer={this.state.measureLayer}
                            />
                        )}
                        {this.renderMenuButton(this.state.displayConvection, 'Weather', () =>
                            this.setState(state => {
                                return { displayConvection: !state.displayConvection };
                            }),
                        )}
                        {this.renderMenuButton(this.state.displayOffload, 'Offload', () =>
                            this.setState(state => {
                                return { displayOffload: !state.displayOffload };
                            }),
                        )}
                        {this.renderMenuButton(this.state.displayOnload, 'OnLoad', () =>
                            this.setState(state => {
                                return { displayOnload: !state.displayOnload };
                            }),
                        )}
                        {this.renderMenuButton(this.state.displayAltRoutes, 'Alt. Routes', () =>
                            this.setState(state => {
                                return { displayAltRoutes: !state.displayAltRoutes };
                            }),
                        )}
                    </Flex>
                </Flex>
            </Flex>
        );
    }

    renderMenuButton(isCurrentlyEnabled: boolean, label: string, handleClick: () => void) {
        let flexStyle: CSSProperties = {
            minWidth: '7em',
            maxWidth: '20em',
            height: '2em',
            textAlign: 'center',
            alignItems: 'stretch',
            justifyContent: 'center',
            gap: '1em', //space between label and settings button
            cursor: 'pointer',
            paddingLeft: 0,
            paddingRight: 0,
        };
        if (isCurrentlyEnabled) {
            flexStyle = Object.assign(flexStyle, {
                background: '#101010',
            });
        }
        return (
            <Flex
                direction={'vertical'}
                style={flexStyle}
                className={`ant-btn detailed-subpanel-title ${isCurrentlyEnabled ? 'active' : ''}`}>
                <span className="iconOnSpan" onClick={handleClick}>
                    {label}
                </span>
            </Flex>
        );
    }

    private airspaceStyleProvider = (airspace: Airspace): null | style.Style => {
        if (
            this.props.selectedScenario.onLoadAirspaceIds &&
            this.props.selectedScenario.onLoadAirspaceIds.find(as => as === airspace.reference.id)
        ) {
            return OLMapStyles.AIRSPACE_STYLE_SCENARIO_ONLOAD.clone();
        } else if (
            this.props.selectedScenario.offLoadAirspaceIds &&
            this.props.selectedScenario.offLoadAirspaceIds.find(as => as === airspace.reference.id)
        ) {
            return OLMapStyles.AIRSPACE_STYLE_SCENARIO_OFFLOAD.clone();
        } else {
            return OLMapStyles.TV_SELECTED_STYLE.clone();
        }
    };
}

export default connector(ScenarioMapPanel);
